/**
 * Decodes the HTML entities in a QTI content fragment, but leaves quote and
 * apostrophe entities encoded.
 *
 * The decode is a `<textarea>` round-trip, which turns every entity into its raw
 * character. That is what we want for `&amp;` (image URLs carry `&amp;`-separated
 * query params) and for `&lt;`/`&gt;`, but not for `&quot;`/`&apos;`: a raw `"` inside
 * an attribute value — an authored image `alt`, say — closes the attribute early, so
 * the description is truncated when the fragment is parsed downstream (`Winter "time"`
 * renders as `Winter `) and screen readers lose the rest of it (SC 1.1.1).
 *
 * Quote entities are therefore shielded by encoding just their leading ampersand
 * (`&quot;` -> `&amp;quot;`). The textarea peels off exactly one layer, so they come back
 * out as `&quot;` / `&#39;` while everything around them decodes normally. Shielding this
 * way rather than swapping in placeholder tokens means there is no sentinel string for
 * authored content to collide with.
 *
 * Entities inside `<math>` elements are left alone — MathML needs its `&lt;`/`&gt;` to
 * survive as entities.
 */
export const decodeHtmlEntities = (content: string): string => {
  // Only decode if content contains HTML entities
  if (!content.includes('&lt;') && !content.includes('&gt;') && !content.includes('&amp;')) {
    return content
  }

  // Split content by math tags to preserve encoded entities inside math expressions.
  // Use a single textarea to avoid any performance issues for very long strings.
  const mathTagRegex = /(<math[\s\S]*?<\/math>)/gi
  const textarea = document.createElement('textarea')

  return content
    .split(mathTagRegex)
    .map(part => {
      // If this part is a math tag, don't decode entities
      if (part.trim().toLowerCase().startsWith('<math')) {
        return part
      }

      // Shield quote entities (named, decimal, hex) behind one extra encoding layer so
      // the decode below returns them as entities instead of raw characters.
      const withQuotesShielded = part
        .replace(/&(?:quot|#0*34|#x0*22);/gi, '&amp;quot;')
        .replace(/&(?:apos|#0*39|#x0*27);/gi, '&amp;#39;')

      // Decode the remaining entities (needed for images).
      textarea.innerHTML = withQuotesShielded

      return textarea.value
    })
    .join('')
}
